import { InputDataNativa } from "../input-data-nativa";
import React, { useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { PeriodicidadeDashboard, formatarParaReais } from "../../services/utilitarios";
import { ajustarPeriodoIsoPorNovaDataFinal, ajustarPeriodoIsoPorNovaDataInicial } from "../../services/periodo";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { ChevronLeft, ChevronRight, Info, Search, X, Calendar as CalendarIcon, ChevronUp, ChevronDown, Check, Loader2 } from "lucide-react";
import { Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import { CalendarioRecebimentos } from "../calendario-locacao";
import { FaCalendarWeek, FaMoneyBill } from "react-icons/fa";
import { BiChart } from "react-icons/bi";
import RadarChartVeiculos, { RadarChartVeiculosProps } from "../grafico-teia";
import { CardAssistenteNotou } from "../assistente/CardAssistenteNotou";

dayjs.locale("pt-br");

// Tipos de dados
export interface CardDetalhamentoItem {
  label: string;
  valor: string | number;
}

interface OrigemCard {
  label: string;
  valor: number;
  iconColor: string;
  link: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface CardProps {
  permissao?: string;
  title: string;
  subtitle: string;
  iconColor?: string;
  carregando: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  link?: string;
  info?: string;
  // Quando definido, o "i" abre um modal com a quebra dos dados em vez de mostrar tooltip.
  detalhamento?: CardDetalhamentoItem[];
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  category?: string;
  origens?: OrigemCard[];
}

interface GridField {
  label: string;
  property: string;
  width?: string;
  render?: (value: any, row: any) => JSX.Element;
  searchValue?: (value: any, row: any) => string;
}

interface PaginationConfig {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
}

interface GridProps {
  permissao?: string;
  title: string;
  fields: GridField[];
  dataSource: any[];
  viewMoreLink?: string;
  carregando: boolean;
  onRowClick?: (rowData: any) => void;
  pagination?: PaginationConfig;
}

interface DashboardProps {
  user: string;
  cards: CardProps[];
  grids: GridProps[];
  graficos: (RadarChartVeiculosProps & { permissao?: string })[];
  mostrarCalendario?: boolean;
  podeVer?: (chave: string) => boolean;
  onPeriodoChange: (periodo: PeriodicidadeDashboard, startDate?: Date, endDate?: Date) => void;
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const skeletonLoading = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Estilos
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) { padding: 20px; }
  @media (max-width: 768px) { padding: 16px; gap: 20px; }
  @media (max-width: 480px) { padding: 12px; gap: 16px; }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
  @media (max-width: 768px) { font-size: 15px; }
`;

const Subtitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  @media (max-width: 768px) { font-size: 22px; }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  @media (max-width: 768px) { flex-direction: column; gap: 12px; }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const CardGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CardGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 10px;
  background-color: #f8fafc;
  transition: all 0.2s ease;
  &:hover { background-color: #f1f5f9; }
`;

const CardGroupTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background: #1f4680;
    border-radius: 2px;
  }
`;

const CardGroupContent = styled.div<{ $isCollapsed: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
  transition: all 0.3s ease;
  overflow: hidden;
  max-height: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '1000px')};
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '8px 0')};
  @media (max-width: 1024px) { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.div<{ hasLink?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 140px;
  width: 100%;
  ${({ hasLink }) => hasLink && `
    cursor: pointer;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
  `}
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: #1f4680;
  }
  a { text-decoration: none; color: inherit; display: flex; flex-direction: column; gap: 12px; height: 100%; }
  @media (max-width: 768px) { padding: 18px; min-height: 130px; }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const IconWrapper = styled.div<{ iconColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: ${({ iconColor }) => iconColor ? `${iconColor}15` : 'rgba(31, 70, 128, 0.1)'};
  border-radius: 10px;
  flex-shrink: 0;
  svg { width: 22px; height: 22px; color: ${({ iconColor }) => iconColor || '#1f4680'}; }
  @media (max-width: 768px) { width: 40px; height: 40px; svg { width: 20px; height: 20px; } }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
`;

const CardTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CardTrend = styled.div<{ trend?: 'up' | 'down' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  margin-top: 4px;
  color: ${({ trend }) => trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#64748b'};
  svg { width: 14px; height: 14px; }
`;

const hexToRgba = (hex: string, alpha: number) => {
  const h = hex.replace('#', '').trim();
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BarraGrossa = styled.div`
  display: flex;
  width: 100%;
  height: 16px;
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
  gap: 2px;
`;

const SegLink = styled.a`
  display: block;
  flex-basis: 0;
  min-width: 8px;
  cursor: pointer;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.1); }
`;

const InfoButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #1f467e;
  transition: color 0.2s ease;
  padding: 4px;
  margin-left: auto;
  display: flex;
  &:hover { color: #64748b; }
  svg { width: 18px; height: 18px; }
`;

const PeriodFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  width: 100%;
  @media (max-width: 768px) { padding: 16px; gap: 12px; }
`;

const DateInputsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
  @media (max-width: 480px) { flex-direction: column; align-items: stretch; }
`;

const PeriodAlert = styled.div`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  padding: 10px 14px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0369a1;
  font-size: 14px;
  margin-top: 8px;
`;

const PeriodButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PeriodButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ $isActive }) => ($isActive ? "#1f4680" : "#f1f5f9")};
  color: ${({ $isActive }) => ($isActive ? "#ffffff" : "#334155")};
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100px;
  justify-content: center;
  &:hover { background: ${({ $isActive }) => ($isActive ? "#173a6d" : "#e2e8f0")}; }
`;

const CalendarIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #f1f5f9;
  border-radius: 8px;
  color: #64748b;
  svg { width: 18px; height: 18px; }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  &::before {
    content: '';
    display: block;
    width: 5px;
    height: 24px;
    background: #1f4680;
    border-radius: 3px;
  }
`;

const SectionNav = styled.div`
  display: flex;
  gap: 10px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #1f4680;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  &:hover { background: #163b6a; transform: scale(1.05); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 12px;
`;

const SliderContent = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  gap: 16px;
  padding: 16px 0;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Slide = styled.div`
  flex: 0 0 calc(100% - 32px);
  scroll-snap-align: start;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  padding: 20px;
  margin: 0 16px;
  width: calc(100% - 32px);
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${({ $active }) => ($active ? "#1f4680" : "#cbd5e1")};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  &:hover { transform: scale(1.2); }
`;

const GridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const GridTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ViewMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #1f4680;
  text-decoration: none;
  &:hover { text-decoration: underline; }
  svg { width: 16px; height: 16px; }
`;

const GridControls = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-width: 250px;
  max-width: 400px;
  input {
    padding: 10px 12px 10px 40px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
    outline: none;
    transition: all 0.2s ease;
    background: #f8fafc;
    &:focus { border-color: #1f4680; background: white; }
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  color: #94a3b8;
  svg { width: 16px; height: 16px; }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  svg { width: 16px; height: 16px; }
`;

const TableContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background: white;
  width: 100%;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  flex-grow: 1;
  max-height: 400px;
  min-height: 400px;
  scrollbar-width: thin;
  &::-webkit-scrollbar { width: 6px; height: 6px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  th, td { padding: 12px 16px; text-align: left; border-bottom: 1px solid #e2e8f0; }
  th { background: #f8fafc; font-weight: 600; font-size: 14px; color: #475569; position: sticky; top: 0; z-index: 2; white-space: nowrap; cursor: pointer; }
  td { font-size: 14px; color: #334155; white-space: nowrap; }
  tr:hover { background-color: #f8fafc; }
  tr:last-child td { border-bottom: none; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
  svg { width: 48px; height: 48px; color: #cbd5e1; }
`;

const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${skeletonLoading} 1.5s infinite;
  border-radius: 12px;
  height: 140px;
  width: 100%;
`;

const SkeletonRowCell = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${skeletonLoading} 1.5s infinite;
  border-radius: 4px;
  height: 20px;
  width: 100%;
`;

const ScrollToSection = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ScrollButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #1f4680;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: #163b6a; transform: translateY(-1px); }
  svg { width: 16px; height: 16px; }
`;

const ChartsAndCalendarContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  width: 100%;
`;

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  overflow: hidden;
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  overflow: hidden;
`;

const CustomPeriodContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  margin-top: 8px;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 180px;
  label { font-size: 13px; color: #64748b; font-weight: 500; }
  input { padding: 8px 12px; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 14px; width: 100%; background: white; height: 36px; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? '#1f4680' : '#d1d5db')};
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  ${({ $primary }) => $primary ? `background: #1f4680; color: white; &:hover { background: #163b6a; }` : `background: white; color: #64748b; &:hover { background: #f8fafc; }`}
`;

const CustomPeriodTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { width: 16px; height: 16px; color: #6b7280; }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 16px;
  background-color: white;
  border-top: 1px solid #e2e8f0;
  gap: 12px;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 90px;
  &:hover:not(:disabled) { background-color: #f3f4f6; border-color: #9ca3af; }
  &:disabled { opacity: 0.7; cursor: not-allowed; background-color: #f9fafb; }
  svg { width: 16px; height: 16px; }
`;

const RotatingSpinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: #1f4680;
`;

const Dashboardv2: React.FC<DashboardProps> = ({ user, cards: todosOsCards, grids: todosOsGrids, graficos: todosOsGraficos, mostrarCalendario = true, podeVer, onPeriodoChange }) => {
  const visivel = (item: { permissao?: string }) => !item.permissao || !podeVer || podeVer(item.permissao);
  const cards = todosOsCards.filter(visivel);
  const grids = todosOsGrids.filter(visivel);
  const graficos = todosOsGraficos.filter(visivel);
  const [sortConfig, setSortConfig] = useState<{ [key: string]: { field: string; direction: 'asc' | 'desc' } }>({});
  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodicidadeDashboard>(PeriodicidadeDashboard.mesAtual);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [cardDetalhamentoAberto, setCardDetalhamentoAberto] = useState<CardProps | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<{ relatorios: HTMLDivElement | null; calendario: HTMLDivElement | null }>({ relatorios: null, calendario: null });
  const [showCustomPeriod, setShowCustomPeriod] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const applyCustomPeriod = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start && end && start <= end) {
        onPeriodoChange(PeriodicidadeDashboard.personalizado, start, end);
      }
    }
  };

  const groupedCards = cards.reduce<Record<string, CardProps[]>>((acc, card) => {
    const category = card.category || 'Geral';
    if (!acc[category]) {
      acc[category] = [];
      if (collapsedCategories[category] === undefined) {
        setCollapsedCategories(prev => ({ ...prev, [category]: false }));
      }
    }
    acc[category].push(card);
    return acc;
  }, {});

  const toggleCategoryCollapse = (category: string) => {
    setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSort = (gridIndex: number, field: string) => {
    setSortConfig((prev) => {
      const currentDirection = prev[gridIndex]?.field === field ? prev[gridIndex].direction === 'asc' ? 'desc' : 'asc' : 'asc';
      return { ...prev, [gridIndex]: { field, direction: currentDirection } };
    });
  };

  const handleSearch = (gridIndex: number, value: string) => {
    setSearchTerms((prev) => ({ ...prev, [gridIndex]: value }));
  };

  const clearSearch = (gridIndex: number) => {
    setSearchTerms((prev) => ({ ...prev, [gridIndex]: '' }));
  };

  const handlePeriodChange = (period: PeriodicidadeDashboard) => {
    setSelectedPeriod(period);
    if (period === PeriodicidadeDashboard.personalizado) {
      setShowCustomPeriod(true);
      setCustomStartDate("");
      setCustomEndDate("");
    } else {
      setShowCustomPeriod(false);
      onPeriodoChange(period);
    }
  };

  const handleRowClick = (gridIndex: number, rowData: any) => {
    if (grids[gridIndex].onRowClick) {
      grids[gridIndex].onRowClick!(rowData);
    }
  };

  const getSortedAndFilteredData = (grid: GridProps, gridIndex: number) => {
    let data = [...grid.dataSource];
    const searchTerm = searchTerms[gridIndex]?.toLowerCase() || '';
    const sort = sortConfig[gridIndex];

    if (searchTerm) {
      data = data.filter((row) =>
        grid.fields.some((field) => {
          const value = field.searchValue ? field.searchValue(row[field.property], row) : row[field.property];
          return String(value).toLowerCase().includes(searchTerm);
        })
      );
    }

    if (sort) {
      data.sort((a, b) => {
        const aValue = a[sort.field];
        const bValue = b[sort.field];
        if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  };

  const periodOptions = [
    { value: PeriodicidadeDashboard.semanaAtual, label: "Semana atual" },
    { value: PeriodicidadeDashboard.mesAtual, label: "Mês Atual" },
    { value: PeriodicidadeDashboard.ultimosQuinzeDias, label: "Últimos 15 dias" },
    { value: PeriodicidadeDashboard.ultimosTrintaDias, label: "Últimos 30 dias" },
    { value: PeriodicidadeDashboard.ultimoAno, label: "Ano Atual" },
    { value: PeriodicidadeDashboard.personalizado, label: "Personalizado" }
  ];

  const nextSlide = () => { if (currentSlide < grids.length - 1) { setCurrentSlide(currentSlide + 1); scrollToSlide(currentSlide + 1); } };
  const prevSlide = () => { if (currentSlide > 0) { setCurrentSlide(currentSlide - 1); scrollToSlide(currentSlide - 1); } };
  const goToSlide = (index: number) => { setCurrentSlide(index); scrollToSlide(index); };

  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.clientWidth;
      sliderRef.current.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollPosition = sliderRef.current.scrollLeft;
      const slideWidth = sliderRef.current.clientWidth;
      const newSlide = Math.round(scrollPosition / slideWidth);
      setCurrentSlide(newSlide);
    }
  };

  const scrollToSection = (sectionId: keyof typeof sectionsRef.current) => {
    const section = sectionsRef.current[sectionId];
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelCustomPeriod = () => {
    setShowCustomPeriod(false);
    setCustomStartDate("");
    setCustomEndDate("");
    setSelectedPeriod(PeriodicidadeDashboard.semanaAtual);
    onPeriodoChange(PeriodicidadeDashboard.semanaAtual);
  };

  const renderOrigens = (card: CardProps) => {
    if (!card.origens || card.origens.length === 0) return null;
    const total = card.origens.reduce((s, o) => s + (o.valor || 0), 0);
    const accent = card.iconColor || '#1f4680';
    return (
      <BarraGrossa>
        {card.origens.map((o, i) => (
          <MuiTooltip
            key={o.label}
            arrow
            placement="top"
            title={`${o.label}: ${formatarParaReais(o.valor || 0)}`}
            componentsProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '13px', padding: '8px 12px' } }, arrow: { sx: { color: '#1e293b' } } }}
          >
            <SegLink
              href={o.link}
              style={{ background: hexToRgba(accent, i === 0 ? 1 : 0.6), flexGrow: total > 0 ? (o.valor || 0) : 1 }}
            />
          </MuiTooltip>
        ))}
      </BarraGrossa>
    );
  };

  return (
    <DashboardContainer>
      <CardAssistenteNotou />
      <Header>
        <HeaderContent>
          <Greeting><Title>Bem-vindo, {user}</Title><Subtitle>Visão Geral</Subtitle></Greeting>
          <ScrollToSection>
            {mostrarCalendario && <ScrollButton onClick={() => scrollToSection('calendario')}><FaCalendarWeek size={16} /> Calendário</ScrollButton>}
            {grids.length > 0 && <ScrollButton onClick={() => scrollToSection('relatorios')}><FaMoneyBill size={16} /> Resumo</ScrollButton>}
            {graficos.length > 0 && <ScrollButton onClick={() => scrollToSection('calendario')}><BiChart size={16} /> Gráficos</ScrollButton>}
          </ScrollToSection>
        </HeaderContent>

        <PeriodFilterContainer>
          <PeriodButtonsContainer>
            <CalendarIconWrapper><CalendarIcon size={18} /></CalendarIconWrapper>
            {periodOptions.map((option) => (
              <PeriodButton key={option.value} $isActive={selectedPeriod === option.value} onClick={() => handlePeriodChange(option.value)}>{option.label}</PeriodButton>
            ))}
          </PeriodButtonsContainer>

          {showCustomPeriod && (
            <CustomPeriodContainer>
              <CustomPeriodTitle><CalendarIcon size={16} /> Definir Período Personalizado</CustomPeriodTitle>
              <DateInputsRow>
                <DateInputGroup><label>Data Inicial</label><InputDataNativa value={customStartDate} onChange={(valor) => { const periodo = ajustarPeriodoIsoPorNovaDataInicial(valor, customEndDate); setCustomStartDate(periodo.dataInicio); setCustomEndDate(periodo.dataFim); }} /></DateInputGroup>
                <DateInputGroup><label>Data Final</label><InputDataNativa value={customEndDate} onChange={(valor) => { const periodo = ajustarPeriodoIsoPorNovaDataFinal(customStartDate, valor); setCustomStartDate(periodo.dataInicio); setCustomEndDate(periodo.dataFim); }} /></DateInputGroup>
                <ButtonGroup>
                  <ActionButton $primary onClick={applyCustomPeriod} disabled={!customStartDate || !customEndDate}><Check size={14} /> Aplicar</ActionButton>
                  <ActionButton onClick={cancelCustomPeriod}><X size={14} /> Cancelar</ActionButton>
                </ButtonGroup>
              </DateInputsRow>
            </CustomPeriodContainer>
          )}
          <PeriodAlert>Atenção: Os dados exibidos abaixo são condicionados ao filtro de período escolhido acima</PeriodAlert>
        </PeriodFilterContainer>

        <CardsContainer>
          {Object.entries(groupedCards).map(([category, categoryCards]) => (
            <CardGroup key={category}>
              <CardGroupHeader onClick={() => toggleCategoryCollapse(category)}>
                <CardGroupTitle>{category}</CardGroupTitle>
                {collapsedCategories[category] ? <ChevronDown size={20} color="#64748b" /> : <ChevronUp size={20} color="#64748b" />}
              </CardGroupHeader>
              <CardGroupContent $isCollapsed={collapsedCategories[category]}>
                {categoryCards.map((card, index) => {
                  const temDetalhamento = !!card.detalhamento && card.detalhamento.length > 0;
                  // Botão "i": tooltip por padrão; com detalhamento, vira clique para abrir modal.
                  const renderInfoBtn = () => temDetalhamento ? (
                    <MuiTooltip title="Clique para ver detalhes" arrow placement="top" componentsProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '13px', padding: '8px 12px' } }, arrow: { sx: { color: '#1e293b' } } }}>
                      <InfoButton
                        as="button"
                        type="button"
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCardDetalhamentoAberto(card);
                        }}
                      >
                        <Info size={16} />
                      </InfoButton>
                    </MuiTooltip>
                  ) : (
                    <MuiTooltip title={card.info || "Informações adicionais"} arrow placement="top" componentsProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '13px', padding: '8px 12px' } }, arrow: { sx: { color: '#1e293b' } } }}>
                      <InfoButton><Info size={16} /></InfoButton>
                    </MuiTooltip>
                  );
                  return (
                  <Card key={index} hasLink={!!card.link}>
                    {card.link ? (
                      <a href={card.link}>
                        <CardHeader>
                          <IconWrapper iconColor={card.iconColor}><card.icon /></IconWrapper>
                          {renderInfoBtn()}
                        </CardHeader>
                        {card.carregando ? <SkeletonCard /> : (
                          <CardContent>
                            <CardTitle>{card.title}</CardTitle>
                            <CardSubtitle>{card.subtitle}</CardSubtitle>
                            {card.trend && <CardTrend trend={card.trend}>{card.trendValue}</CardTrend>}
                            {renderOrigens(card)}
                          </CardContent>
                        )}
                      </a>
                    ) : (
                      <>
                        <CardHeader>
                          <IconWrapper iconColor={card.iconColor}><card.icon /></IconWrapper>
                          {renderInfoBtn()}
                        </CardHeader>
                        {card.carregando ? <SkeletonCard /> : (
                          <CardContent>
                            <CardTitle>{card.title}</CardTitle>
                            <CardSubtitle>{card.subtitle}</CardSubtitle>
                            {card.trend && <CardTrend trend={card.trend}>{card.trendValue}</CardTrend>}
                            {renderOrigens(card)}
                          </CardContent>
                        )}
                      </>
                    )}
                  </Card>
                  );
                })}
              </CardGroupContent>
            </CardGroup>
          ))}
        </CardsContainer>
      </Header>

      <MainContent>
        {grids.length > 0 && (
        <Section id="relatorios" ref={el => { sectionsRef.current.relatorios = el as HTMLDivElement | null; }}>
          <SectionHeader><SectionTitle>Resumo Financeiro</SectionTitle></SectionHeader>
          <SliderControls>
            <DotsContainer>{grids.map((_, index) => (<Dot key={index} $active={index === currentSlide} onClick={() => goToSlide(index)} />))}</DotsContainer>
            <SectionNav>
              <NavButton onClick={prevSlide} disabled={currentSlide === 0}><ChevronLeft /></NavButton>
              <NavButton onClick={nextSlide} disabled={currentSlide === grids.length - 1}><ChevronRight /></NavButton>
            </SectionNav>
          </SliderControls>

          <SliderContainer>
            <SliderContent ref={sliderRef} onScroll={handleScroll}>
              {grids.map((grid, index) => (
                <Slide key={index}>
                  <React.Fragment>
                    <GridHeader>
                      <GridTitle>{grid.title}</GridTitle>
                      {grid.viewMoreLink && <ViewMoreLink href={grid.viewMoreLink}>Ver mais <ChevronRight size={16} /></ViewMoreLink>}
                    </GridHeader>

                    <GridControls>
                      <SearchContainer>
                        <SearchIcon><Search size={16} /></SearchIcon>
                        <input type="text" placeholder="Pesquisar..." value={searchTerms[index] || ''} onChange={(e) => handleSearch(index, e.target.value)} />
                        {searchTerms[index] && <ClearButton onClick={() => clearSearch(index)}><X size={16} /></ClearButton>}
                      </SearchContainer>
                    </GridControls>

                    <TableContainer>
                      <TableWrapper>
                        <Table>
                          <thead>
                            <tr>
                              {grid.fields.map((field, idx) => (
                                <th key={idx} onClick={() => handleSort(index, field.property)}>
                                  {field.label}
                                  {sortConfig[index]?.field === field.property && (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width={16} height={16}>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sortConfig[index].direction === 'asc' ? 'M19 9l-7 7-7-7' : 'M5 15l7-7 7 7'} />
                                    </svg>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {grid.carregando ? (
                              // Exibe a quantidade de linhas configurada na paginação ou 10 por padrão para manter altura
                              [...Array(grid.pagination?.pageSize || 10)].map((_, skelIndex) => (
                                <tr key={`skeleton-${skelIndex}`}>
                                  {grid.fields.map((_, colIndex) => (
                                    <td key={colIndex}>
                                      <SkeletonRowCell />
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : getSortedAndFilteredData(grid, index).length > 0 ? (
                              getSortedAndFilteredData(grid, index).map((data, rowIndex) => (
                                <tr key={rowIndex} onClick={() => grid.onRowClick && handleRowClick(index, data)}>
                                  {grid.fields.map((field, colIndex) => (
                                    <td key={colIndex}>
                                      {field.render ? field.render(data[field.property], data) : data[field.property]}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={grid.fields.length}>
                                  <EmptyState>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p>Nenhum registro encontrado</p>
                                  </EmptyState>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </TableWrapper>

                      {grid.pagination && grid.pagination.totalCount > grid.pagination.pageSize && (
                        <PaginationContainer>
                          <PageButton onClick={() => grid.pagination?.onPageChange(grid.pagination.page - 1)} disabled={grid.pagination.page === 1 || grid.carregando}>
                             {grid.carregando ? <RotatingSpinner size={16} /> : <><ChevronLeft size={16} /> Anterior</>}
                          </PageButton>
                          <PageInfo>
                            Página {grid.pagination.page} de {Math.ceil(grid.pagination.totalCount / grid.pagination.pageSize)}
                          </PageInfo>
                          <PageButton onClick={() => grid.pagination?.onPageChange(grid.pagination.page + 1)} disabled={grid.pagination.page >= Math.ceil(grid.pagination.totalCount / grid.pagination.pageSize) || grid.carregando}>
                             {grid.carregando ? <RotatingSpinner size={16} /> : <>Próximo <ChevronRight size={16} /></>}
                          </PageButton>
                        </PaginationContainer>
                      )}
                    </TableContainer>
                  </React.Fragment>
                </Slide>
              ))}
            </SliderContent>
          </SliderContainer>
        </Section>
        )}

        {(mostrarCalendario || graficos.length > 0) && (
        <Section id="calendario" ref={el => { sectionsRef.current.calendario = el as HTMLDivElement | null; }}>
          <SectionHeader><SectionTitle>{mostrarCalendario && graficos.length > 0 ? 'Calendário e Gráficos' : mostrarCalendario ? 'Calendário' : 'Gráficos'}</SectionTitle></SectionHeader>
          <ChartsAndCalendarContainer>
            {mostrarCalendario && (
            <CalendarContainer>
              <CalendarioRecebimentos legenda={[{ status: "Pago", cor: "#4CAF50" }, { status: "A vencer", cor: "#FFC107" }, { status: "Em atraso", cor: "#ef4444" },]} />
            </CalendarContainer>
            )}
            {graficos.map((grafico, index) => (
              <ChartContainer key={index}>
                <RadarChartVeiculos titulo={grafico.titulo} subtitulo={grafico.subtitulo} name={grafico.name} dados={grafico.dados || []} />
              </ChartContainer>
            ))}
          </ChartsAndCalendarContainer>
        </Section>
        )}
      </MainContent>

      <Dialog
        open={!!cardDetalhamentoAberto}
        onClose={() => setCardDetalhamentoAberto(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>{cardDetalhamentoAberto?.title}</span>
          <IconButton size="small" onClick={() => setCardDetalhamentoAberto(null)} aria-label="Fechar"><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {cardDetalhamentoAberto?.info && (
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>{cardDetalhamentoAberto.info}</div>
          )}
          {cardDetalhamentoAberto?.detalhamento && cardDetalhamentoAberto.detalhamento.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cardDetalhamentoAberto.detalhamento.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span style={{ color: '#475569', fontWeight: 500 }}>{item.label}</span>
                  <span style={{ color: '#1f2937', fontWeight: 600 }}>{item.valor}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#64748b', fontSize: 13 }}>Sem dados para detalhar.</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardDetalhamentoAberto(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
};

export default Dashboardv2;